/**
 * @fileoverview Multi unidades v2 — visual SAAS premium.
 *
 * Filosofia: cockpit executivo. Hierarquia em tiers S/A/B/C com
 * NOW separado de histórico. Accent monocromático #5B7CFF. Cores
 * semantic (healthy/watch/breach) reservadas pra status SLA. Dark
 * glass como ar visual. Densidade alta mas legível.
 *
 * Endpoint único: GET /dashboard/multi-unidades-aggregate (cache 30s).
 */

import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef
} from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  RefreshCw,
  Download,
  Maximize2,
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  MessageSquare,
  Calendar,
  Info,
  X as XIcon
} from "lucide-react";
import Dialog from "@material-ui/core/Dialog";
import { AuthContext } from "../../../context/Auth/AuthContext";
import api from "../../../services/api";
import useDepartmentScope from "../../../hooks/useDepartmentScope";
import DepartmentScopeSelect from "../../../components/DepartmentScopeSelect";
import { i18n } from "../../../translate/i18n";
import { useDebounce } from "../../../hooks/useDebounce";
import { getMUv2Tokens, slaStatus } from "./theme";
import Sparkline from "./components/Sparkline";
import StatusBadge from "./components/StatusBadge";
import DeltaPill from "./components/DeltaPill";
import GlassTooltip from "./components/GlassTooltip";
import BarInCell from "./components/BarInCell";
import LivePulseDot from "./components/LivePulseDot";
import CardDetailsDialog from "./components/CardDetailsDialog";
import { dayMonth, fmtNumberPtBR, formatARTMinutes, openableProps, secToShort } from "./format";

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, "0");

// O agregado tem cache de 30s no backend e volta em ~2ms: sem um piso de
// tempo o loading nem chega a ser desenhado e a tela parece nao ter
// reagido ao filtro.
const MIN_LOADING_MS = 520;

// Depois de um "Atualizar agora", o botão espera 10s: impede o clique em
// sequência sem prender quem quer os números novos logo depois.
const REFRESH_COOLDOWN_MS = 10_000;

// Cores do ART, em segundos, pelos limites de cada conta (Configurações →
// Indicadores → Tempo de resposta), que o servidor manda em `artThresholds`.
// Com várias contas, valem os mais rigorosos entre elas, como no ART do grupo.
// Sem nenhum, os padrões da configuração (10 e 20 min).
const artLimitsFrom = thresholds => ({
  ok: (thresholds?.yellowMin ?? 10) * 60,
  warn: (thresholds?.redMin ?? 20) * 60
});
const artLimitsFor = (byCompany, companyIds) => {
  const list = companyIds.map(id => byCompany[id]).filter(Boolean);
  if (list.length === 0) return null;
  return artLimitsFrom({
    yellowMin: Math.min(...list.map(th => th.yellowMin)),
    redMin: Math.min(...list.map(th => th.redMin))
  });
};
const sameLimits = (a, b) => a.ok === b.ok && a.warn === b.warn;
const limitsText = limits => `verde até ${limits.ok / 60} min, amarelo até ${limits.warn / 60} min`;

// As contas que valem para os limites de uma pessoa: as em que ela tem resposta
// medida (o ART dela sai só delas); sem nenhuma, todas as dela. A lista antiga
// de atendentes não tem breakdown, só a conta da linha.
const accountsOf = row => {
  const all = row.breakdown || [];
  const measured = all.filter(b => b.avgSupportTime != null || b.pairCount > 0);
  const ids = (measured.length ? measured : all).map(b => b.companyId);
  return ids.length ? ids : row.companyId != null ? [row.companyId] : [];
};

// Status do ART do grupo que o servidor calcula: o pior entre as contas, cada
// uma pelos limites dela.
const SERVER_ART_STATUS = { green: "healthy", yellow: "watch", red: "breach", empty: "empty" };

// A explicação do cabeçalho Status sai dos mesmos limites que pintam a coluna.
// Contas com limites diferentes: a regra, sem números (cada linha diz os seus).
const STATUS_HELP_INTRO =
  "Mostra se a pessoa responde rápido aos clientes, pelo tempo médio que leva para responder no período (conta só o horário de expediente).\n\n";
const STATUS_HELP_EMPTY =
  "Sem medição: nenhuma resposta a cliente no período, ou a conta sem horário de atendimento configurado\n\n";
const statusHelp = limits =>
  limits
    ? STATUS_HELP_INTRO +
      `Saudável: até ${limits.ok / 60} min\n` +
      `Atenção: de ${limits.ok / 60} a ${limits.warn / 60} min\n` +
      `Crítico: mais de ${limits.warn / 60} min\n` +
      STATUS_HELP_EMPTY +
      "Os limites vêm de Configurações → Indicadores → Tempo de resposta."
    : STATUS_HELP_INTRO +
      "Saudável: até o limite do verde\n" +
      "Atenção: até o limite do amarelo\n" +
      "Crítico: acima do limite do amarelo\n" +
      STATUS_HELP_EMPTY +
      "Cada pessoa segue os limites da conta dela, em Configurações → Indicadores → Tempo de resposta. Passe o mouse no status para ver os de cada uma.";

const secToHms = sec => {
  if (sec == null || sec <= 0) return "—";
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return h + ":" + pad2(m) + ":" + pad2(r);
  return pad2(m) + ":" + pad2(r);
};


// "Atualizado agora", "Atualizado há 7 min", "Atualizado há 2 h"; sem data, nada.
const updatedAgo = iso => {
  if (!iso) return "";
  const s = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "Atualizado agora";
  const m = Math.floor(s / 60);
  if (m < 60) return "Atualizado há " + m + " min";
  const h = Math.floor(m / 60);
  if (h < 24) return "Atualizado há " + h + " h";
  const d = Math.floor(h / 24);
  return "Atualizado há " + d + (d === 1 ? " dia" : " dias");
};

// Gera array sintético de 12 pontos pra sparkline quando não tem
// histórico real ainda (visual placeholder coerente).
const placeholderSpark = (target, variance = 0.3) => {
  const arr = [];
  for (let i = 0; i < 12; i++) {
    const wave = Math.sin((i / 12) * Math.PI * 2) * variance;
    arr.push(Math.max(0, Math.round(target * (1 - variance / 2 + wave))));
  }
  arr[arr.length - 1] = target;
  return arr;
};

// ─────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => {
  const t = getMUv2Tokens(theme);
  return {
    root: {
      minHeight: "calc(100vh - 56px)",
      background: t.pageBg,
      fontFamily: "'Montserrat', sans-serif",
      color: t.textPrimary,
      paddingBottom: 48
    },

    // Z0 Top Bar
    topBar: {
      position: "sticky",
      top: 0,
      height: 56,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 24px",
      background: t.pageBg,
      borderBottom: "1px solid " + t.cardBorder,
      zIndex: 50
    },
    breadcrumb: {
      fontSize: 13,
      fontWeight: 600,
      color: t.textSecondary,
      letterSpacing: "0.01em",
      "& span": { color: t.textPrimary }
    },
    updatedAgo: {
      marginTop: 2,
      minHeight: 16,
      fontSize: 12,
      color: t.textMuted,
      fontWeight: 500
    },
    actionsCluster: { display: "flex", gap: 4 },
    actionBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 10px",
      borderRadius: 8,
      background: "transparent",
      border: "1px solid transparent",
      color: t.textSecondary,
      cursor: "pointer",
      transition: "all 160ms ease",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      "&:hover:not(:disabled)": {
        background: t.surfaceBg,
        borderColor: t.cardBorder,
        color: t.textPrimary
      },
      // Sem eventos no botão desabilitado, o mouse chega ao span do tooltip.
      "&:disabled": { opacity: 0.45, pointerEvents: "none" }
    },

    // Content wrap
    contentWrap: { maxWidth: 1440, margin: "0 auto", padding: "24px" },

    // Z1 Hero Strip
    heroStrip: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 16,
      marginBottom: 24,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" }
    },
    // Quadro que abre o modal do que está por trás dele.
    clickable: {
      cursor: "pointer",
      "&:focus-visible": { outline: "1px solid " + t.accent, outlineOffset: 2 }
    },
    heroCard: {
      padding: 24,
      background: t.cardBgStrong,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 168,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
      transition: "all 220ms ease",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: t.cardBorderHover,
        boxShadow: t.cardShadowHover
      }
    },
    heroHeaderRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start"
    },
    heroLabel: {
      textTransform: "uppercase",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: t.textMuted
    },
    heroValueRow: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      marginTop: 8
    },
    heroValue: {
      fontSize: 38,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: t.textPrimary,
      lineHeight: 1,
      letterSpacing: "-0.02em"
    },
    heroValueBreach: { color: t.breachText },
    heroValueWatch: { color: t.watchText },
    heroFooterRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginTop: 12
    },
    heroSparkSlot: { width: 96, height: 28, opacity: 0.85 },

    // Top offenders (card 4)
    topOffList: { display: "flex", flexDirection: "column", gap: 8 },
    topOffItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "4px 0",
      borderBottom: "1px dashed " + t.cardBorder,
      fontSize: 12,
      "&:last-child": { borderBottom: "none" }
    },
    topOffName: {
      color: t.textPrimary,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: 140
    },
    topOffArt: {
      fontVariantNumeric: "tabular-nums",
      fontWeight: 700,
      fontSize: 12
    },

    // Z2 Filter Bar
    filterBar: {
      position: "sticky",
      top: 56,
      padding: "12px 0",
      background: t.pageBg,
      borderBottom: "1px solid " + t.cardBorder,
      zIndex: 40,
      display: "flex",
      gap: 12,
      alignItems: "center",
      marginBottom: 16,
      flexWrap: "wrap"
    },
    periodSegmented: {
      display: "inline-flex",
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 8,
      padding: 3
    },
    periodBtn: {
      padding: "6px 14px",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 600,
      color: t.textMuted,
      background: "transparent",
      border: "none",
      fontFamily: "inherit",
      transition: "all 160ms ease",
      "&:hover": { color: t.textPrimary }
    },
    periodBtnActive: {
      background: t.accentSoft,
      color: t.accent
    },
    dateInput: {
      padding: "7px 10px",
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 8,
      color: t.textPrimary,
      fontSize: 12,
      fontFamily: "inherit",
      colorScheme: "dark",
      transition: "all 160ms ease",
      "&:focus": {
        outline: "none",
        borderColor: t.accentBorder,
        background: t.cardBg
      }
    },
    // "Números de": as unidades ou um departamento (useDepartmentScope)
    scope: { display: "inline-flex", alignItems: "center", gap: 8 },
    scopeLabel: { fontSize: 12, fontWeight: 600, color: t.textMuted },
    scopeSelect: {
      minWidth: 180,
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        background: t.surfaceBg,
        fontSize: 12,
        fontWeight: 600
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: t.cardBorder },
      "& .MuiSelect-select": { padding: "9px 30px 9px 12px" },
      "& .MuiSelect-icon": { fontSize: 18 }
    },
    searchWrap: { position: "relative", marginLeft: "auto" },
    searchInput: {
      width: 240,
      padding: "8px 12px 8px 34px",
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 8,
      color: t.textPrimary,
      fontSize: 12,
      fontFamily: "inherit",
      transition: "all 160ms ease",
      "&::placeholder": { color: t.textMuted },
      "&:focus": {
        outline: "none",
        borderColor: t.accentBorder,
        background: t.cardBg
      }
    },
    searchIcon: {
      position: "absolute",
      left: 10,
      top: "50%",
      transform: "translateY(-50%)",
      color: t.textMuted,
      pointerEvents: "none"
    },

    // Z3 Performance Table
    tableCard: {
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      marginBottom: 24,
      overflow: "hidden"
    },
    tableHeader: {
      display: "grid",
      gridTemplateColumns:
        "20px minmax(170px,1fr) 116px 120px 96px 120px 80px 40px",
      columnGap: 28,
      // Abaixo de 1280px, o respiro maior cortaria as últimas colunas.
      [theme.breakpoints.down("md")]: { columnGap: 12 },
      padding: "10px 16px",
      background: t.cardBgStrong,
      borderBottom: "1px solid " + t.cardBorder,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: t.textMuted,
      alignItems: "center"
    },
    headerWithHelp: { display: "inline-flex", alignItems: "center", gap: 4 },
    // O selo do status na largura do texto, com ou sem tooltip.
    statusCell: {
      justifySelf: "start",
      display: "inline-flex",
      borderRadius: 999,
      "&:focus-visible": { outline: "1px solid " + t.accent, outlineOffset: 2 }
    },
    helpIcon: {
      display: "inline-flex",
      padding: 0,
      border: 0,
      background: "none",
      color: "inherit",
      cursor: "help",
      borderRadius: 4,
      "&:hover": { color: t.textPrimary },
      "&:focus-visible": { outline: "1px solid " + t.accent, outlineOffset: 2, color: t.textPrimary }
    },
    sortableHeader: {
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      userSelect: "none",
      "&:hover": { color: t.textPrimary }
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns:
        "20px minmax(170px,1fr) 116px 120px 96px 120px 80px 40px",
      columnGap: 28,
      // Abaixo de 1280px, o respiro maior cortaria as últimas colunas.
      [theme.breakpoints.down("md")]: { columnGap: 12 },
      padding: "10px 16px",
      borderBottom: "1px solid " + t.cardBorder,
      alignItems: "center",
      transition: "all 160ms ease",
      "&:hover": { background: t.accentSoft },
      "&:last-child": { borderBottom: "none" }
    },
    tableCellName: {
      fontSize: 13,
      fontWeight: 600,
      color: t.textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    tableCellNum: {
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: t.textSecondary,
      fontWeight: 500
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block"
    },

    // Z4 Charts Strip
    chartsHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
      marginTop: 8
    },
    chartsTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: t.textPrimary,
      letterSpacing: "-0.01em"
    },
    chartsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 16,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
    },
    chartCard: {
      padding: "16px 18px",
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      transition: "all 180ms ease",
      "&:hover": {
        borderColor: t.cardBorderHover,
        boxShadow: t.cardShadow
      }
    },
    chartHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
      gap: 8
    },
    chartTitle: {
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: t.textMuted,
      fontWeight: 700
    },
    chartValue: {
      fontSize: 24,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: t.textPrimary,
      letterSpacing: "-0.02em"
    },
    chartBody: { flex: 1, minHeight: 120, position: "relative" },

    // Refetch feedback — barra indeterminada colada na base da filter bar
    loadingBar: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: -1,
      height: 2,
      overflow: "hidden",
      background: t.accentSoft,
      pointerEvents: "none"
    },
    loadingBarFill: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "40%",
      background: t.accent,
      borderRadius: 2,
      animation: "muv2-progress 1100ms ease-in-out infinite"
    },
    dataZone: { transition: "opacity 180ms ease" },
    dataDimmed: { opacity: 0.42, pointerEvents: "none" },

    // Chip do periodo efetivamente refletido nos numeros
    titleCluster: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0
    },
    rangeChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "2px 8px",
      borderRadius: 999,
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      fontSize: 11,
      fontWeight: 600,
      color: t.textSecondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      transition: "all 180ms ease"
    },
    rangeChipBusy: {
      background: t.accentSoft,
      borderColor: t.accentBorder,
      color: t.accent
    },

    // States
    skeletonCard: {
      padding: 24,
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 168,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(110deg, transparent 35%, " +
          t.accentSoft +
          " 50%, transparent 65%)",
        animation: "muv2-shimmer 1600ms ease-in-out infinite"
      }
    },
    "@global": {
      "@keyframes muv2-shimmer": {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" }
      }
    },
    emptyState: {
      padding: 48,
      textAlign: "center",
      color: t.textMuted,
      fontSize: 13,
      fontWeight: 500
    },
    errorCard: {
      padding: 16,
      background: t.breachBg,
      border: "1px solid " + t.breachBorder,
      borderRadius: 12,
      color: t.breachText,
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24
    },
    retryBtn: {
      background: t.breachBorder,
      color: "#fff",
      padding: "6px 12px",
      borderRadius: 6,
      border: "none",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      fontFamily: "inherit",
      marginLeft: "auto",
      "&:hover": { background: t.breach }
    },

    // Spinning refresh
    refreshSpinning: { animation: "muv2-spin 800ms linear infinite" }
  };
});

// Inject spin/progress keyframes (escape do "@global" pattern bug do MUI v4)
const GlobalSpinStyle = () => (
  <style>{`@keyframes muv2-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes muv2-progress { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }`}</style>
);

// A mensagem do erro da API: o código traduzido (backendErrors), senão o texto dela.
const apiErrorText = (e, fallback) => {
  const code = e?.response?.data?.error;
  if (code && i18n.exists(`backendErrors.${code}`)) return i18n.t(`backendErrors.${code}`);
  return code || e?.message || fallback;
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────
const MultiUnitV2 = ({ groupName: propGroupName }) => {
  const theme = useTheme();
  const tokens = getMUv2Tokens(theme);
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const groupName = propGroupName || user?.company?.groupName || "";
  // Os números de: as unidades que a pessoa vê (a tela de sempre) ou os
  // departamentos dela (a soma das contas de cada um).
  const departmentScope = useDepartmentScope(true);
  const { departmentIds, ready } = departmentScope;
  const scopeParams = useMemo(
    () => (departmentIds ? { departmentIds } : groupName ? { groupName } : {}),
    [departmentIds, groupName]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Loading "visivel": so nos refetches que o usuario provocou (troca de
  // periodo/data ou clique no refresh). O auto-refresh de 60s continua
  // silencioso pra tela nao piscar sozinha.
  const [refetching, setRefetching] = useState(false);
  const [refreshLocked, setRefreshLocked] = useState(false);
  const refreshLockTimerRef = useRef(null);
  const [error, setError] = useState(null);
  // Abre em "Hoje" (pedido do usuário, 28/10/2026).
  const [period, setPeriod] = useState("hoje"); // hoje | 7d | 30d | custom
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD (filtro livre)
  const [dateTo, setDateTo] = useState(""); // YYYY-MM-DD
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("tickets");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null); // atendente selecionado pro modal
  const [openCard, setOpenCard] = useState(null); // quadro com o modal aberto
  const [drillKey, setDrillKey] = useState(null); // `${userId}-${companyId}` aberto no modal
  const [drillCache, setDrillCache] = useState({}); // key -> { loading, tickets, error }
  const [, setNowTick] = useState(0);
  const PAGE_SIZE = 15;
  // Quadro clicável (mouse e teclado): abre o modal do que está por trás dele.
  const openable = key => openableProps(() => setOpenCard(key));
  const MAX_RANGE_DAYS = 31;
  const fetchSeqRef = useRef(0);
  const minLoadingTimerRef = useRef(null);
  const lastFetchAtRef = useRef(0);

  // Quantidade de dias do range custom (inclusivo). Limite de 31 dias.
  const daysOf = (from, to) =>
    from && to ? Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1 : 0;
  const rangeValid = (from, to) => {
    const days = daysOf(from, to);
    return !!from && !!to && days >= 1 && days <= MAX_RANGE_DAYS;
  };
  const rangeDays = daysOf(dateFrom, dateTo);
  const rangeTooLong = period === "custom" && !!dateFrom && !!dateTo && !rangeValid(dateFrom, dateTo);
  // Custom só ativa com as 2 datas E dentro do limite de dias.
  const customActive = period === "custom" && rangeValid(dateFrom, dateTo);
  // O pedido espera 400 ms depois da última data digitada: um pedido por
  // escolha, não um por tecla. Os avisos ao lado das datas seguem na hora.
  const queryDateFrom = useDebounce(dateFrom, 400);
  const queryDateTo = useDebounce(dateTo, 400);
  const queryCustomActive = period === "custom" && rangeValid(queryDateFrom, queryDateTo);

  const fetchData = useCallback(({ silent = false } = {}) => {
    // Range custom pela metade ou fora do limite: nao busca. Sem isso o
    // backend ignora as datas e devolve os ultimos 7 dias — os numeros
    // mudam sem o usuario entender de que periodo sao.
    if (period === "custom" && !queryCustomActive) {
      // O pedido em andamento é de outro filtro: não pode chegar à tela, e o
      // "carregando" dele não pode ficar ligado.
      fetchSeqRef.current += 1;
      setLoading(false);
      setRefetching(false);
      setError(null);
      return;
    }
    if (!ready) return;

    const seq = ++fetchSeqRef.current;
    const startedAt = Date.now();
    lastFetchAtRef.current = startedAt;
    if (minLoadingTimerRef.current) {
      clearTimeout(minLoadingTimerRef.current);
      minLoadingTimerRef.current = null;
    }
    setLoading(true);
    if (!silent) setRefetching(true);
    setError(null);
    const params = { period, ...scopeParams };
    if (period === "custom") {
      params.date_from = queryDateFrom;
      params.date_to = queryDateTo;
    }
    api
      .get("/dashboard/multi-unidades-aggregate", { params })
      .then(r => {
        if (seq === fetchSeqRef.current) setData(r.data);
      })
      .catch(e => {
        if (seq !== fetchSeqRef.current) return; // resposta obsoleta
        setError(apiErrorText(e, "Erro ao carregar dados."));
      })
      .finally(() => {
        if (seq !== fetchSeqRef.current) return;
        setLoading(false);
        const remaining = MIN_LOADING_MS - (Date.now() - startedAt);
        if (remaining <= 0) {
          setRefetching(false);
        } else {
          minLoadingTimerRef.current = setTimeout(
            () => setRefetching(false),
            remaining
          );
        }
      });
  }, [scopeParams, ready, period, queryDateFrom, queryDateTo, queryCustomActive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshNow = () => {
    if (refreshLocked) return;
    fetchData();
    setRefreshLocked(true);
    refreshLockTimerRef.current = setTimeout(() => setRefreshLocked(false), REFRESH_COOLDOWN_MS);
  };
  useEffect(() => () => clearTimeout(refreshLockTimerRef.current), []);

  // Tick a cada 15s pra refresh do "Atualizado há"
  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // Auto refresh a cada 60s (silencioso — sem barra de loading). Com a aba
  // oculta, não pede (ninguém está olhando); ao voltar, relê se já passou 1 min.
  useEffect(() => {
    const id = setInterval(() => {
      // Sem repetir o pedido feito há pouco (ao voltar para a aba, por exemplo).
      if (!document.hidden && Date.now() - lastFetchAtRef.current >= 55_000) fetchData({ silent: true });
    }, 60_000);
    const onVisibility = () => {
      if (!document.hidden && Date.now() - lastFetchAtRef.current >= 60_000) fetchData({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchData]);

  // ART com alguma conta de fora (passou do tempo no servidor, que segue
  // calculando): relê uma vez em 15 s, em silêncio. Se vier incompleto de novo,
  // fica para o polling de sempre — sem ciclo de 15 s. A tentativa só conta
  // quando dispara, e volta a valer com outro filtro.
  const artRetriesRef = useRef(0);
  useEffect(() => {
    artRetriesRef.current = 0;
  }, [fetchData]);
  useEffect(() => {
    if (!data?.meta?.artIncomplete) {
      artRetriesRef.current = 0;
      return undefined;
    }
    if (artRetriesRef.current >= 1) return undefined;
    const id = setTimeout(() => {
      artRetriesRef.current += 1;
      fetchData({ silent: true });
    }, 15_000);
    return () => clearTimeout(id);
  }, [data, fetchData]);

  useEffect(
    () => () => {
      if (minLoadingTimerRef.current) clearTimeout(minLoadingTimerRef.current);
    },
    []
  );

  // ── Derived ──
  const tierS = data?.tierS;
  const performance = data?.performance || [];
  // Backend não conseguiu montar a tabela: mostrar o erro, nunca números zerados.
  const performanceError = !!data?.meta?.performanceError;
  const charts = data?.charts;
  const artRanking = data?.artRanking || [];

  const filteredAttendants = useMemo(() => {
    let arr = performance;
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(a => (a.name || "").toLowerCase().includes(q));
    }
    arr = [...arr].sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [performance, search, sortBy, sortDir]);

  // Reset pra pagina 1 sempre que filtro/sort muda
  useEffect(() => { setPage(1); }, [search, sortBy, sortDir, period, dateFrom, dateTo, scopeParams]);

  // Drill-down do ART (lazy): abre/fecha a lista de tickets de uma conta.
  // Cache por key `${userId}-${companyId}` — só busca no servidor 1x por
  // (conta, filtro). Invalidado quando muda atendente/período/range/escopo.
  // O detalhe vai pelo período dos números que estão na tela (o que o servidor
  // devolveu), não pelo filtro que ainda está sendo escolhido. A geração muda
  // com o atendente ou com esses números, e a resposta de uma geração anterior
  // não entra no cache novo.
  const shownPeriod = data?.meta?.period;
  const shownFrom = data?.meta?.rangeFrom;
  const shownTo = data?.meta?.rangeTo;
  const drillGenRef = useRef(0);
  const drillRequestedRef = useRef(new Set());
  useEffect(() => {
    drillGenRef.current += 1;
    drillRequestedRef.current = new Set();
    setDrillKey(null);
    setDrillCache({});
  }, [detailRow, shownPeriod, shownFrom, shownTo, scopeParams]);

  const toggleDrill = useCallback((b) => {
    const key = `${b.userId}-${b.companyId}`;
    setDrillKey(prev => (prev === key ? null : key));
    if (drillRequestedRef.current.has(key)) return; // já carregado/carregando
    drillRequestedRef.current.add(key);
    const generation = drillGenRef.current;
    const params = { userId: b.userId, companyId: b.companyId, period: shownPeriod || period, ...scopeParams };
    if (shownPeriod === "custom" && shownFrom && shownTo) {
      params.date_from = shownFrom;
      params.date_to = shownTo;
    }
    setDrillCache(c => ({ ...c, [key]: { loading: true } }));
    api
      .get("/dashboard/multi-unidades/attendant-tickets", { params })
      .then(r => {
        if (generation !== drillGenRef.current) return;
        setDrillCache(c => ({
          ...c,
          [key]: {
            loading: false,
            tickets: r.data?.tickets || [],
            scheduleConfigured: r.data?.scheduleConfigured !== false
          }
        }));
      })
      .catch(e => {
        if (generation !== drillGenRef.current) return;
        // Pode tentar de novo: fechar e abrir a linha pede outra vez.
        drillRequestedRef.current.delete(key);
        setDrillCache(c => ({
          ...c,
          [key]: { loading: false, error: apiErrorText(e, "Erro ao carregar tickets") }
        }));
      });
  }, [scopeParams, period, shownPeriod, shownFrom, shownTo]);

  const totalPages = Math.max(1, Math.ceil(filteredAttendants.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedAttendants = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredAttendants.slice(start, start + PAGE_SIZE);
  }, [filteredAttendants, safePage]);

  const maxTickets = useMemo(
    () => Math.max(1, ...performance.map(a => Number(a.tickets) || 0)),
    [performance]
  );

  const toggleSort = field => {
    if (sortBy === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const renderSortIcon = field => {
    if (sortBy !== field) return null;
    return sortDir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />;
  };

  // ── Hero values + status ──
  const activeNow = tierS?.ativosAgora ?? 0;
  const noResponse = tierS?.semResposta ?? 0;
  const noResponseRate = tierS?.semRespostaRate ?? 0;
  const noResponseStatus = noResponse === 0 ? "healthy" : noResponse < 5 ? "watch" : "breach";
  const artMin = tierS?.art?.avgMinutes ?? null;
  const artSec = artMin != null ? artMin * 60 : 0;
  const artThresholds = data?.artThresholds || {};
  const allCompanyIds = Object.keys(artThresholds).map(Number);
  // Os mais rigorosos entre as contas: reserva de quem não tem conta conhecida.
  const artLimits =
    artLimitsFor(artThresholds, allCompanyIds) || artLimitsFrom(tierS?.art?.thresholds);
  // Uma pessoa: os limites das contas dela (com várias, o mais rigoroso).
  const limitsOf = row => artLimitsFor(artThresholds, accountsOf(row)) || artLimits;
  const limitsVary = allCompanyIds.some(
    id => !sameLimits(artLimitsFrom(artThresholds[id]), artLimits)
  );
  const artStatus =
    SERVER_ART_STATUS[tierS?.art?.status] || slaStatus(artSec, artLimits);

  // ── Loading visivel ──
  // So depois que ja existe conteudo na tela; no 1o load os skeletons ja
  // fazem esse papel.
  const busy = refetching && !!data;
  // Marca as zonas de dados (nao a filter bar) enquanto o refetch corre.
  const zone = base =>
    `${base} ${classes.dataZone}${busy ? ` ${classes.dataDimmed}` : ""}`;

  // Periodo que os numeros na tela realmente refletem (vem do backend, que
  // clampa range > 31 dias). Fallback pro filtro local se a meta nao vier.
  const appliedRange = (() => {
    const from = data?.meta?.rangeFrom;
    const to = data?.meta?.rangeTo;
    if (from && to) {
      return from === to ? dayMonth(from) : dayMonth(from) + " → " + dayMonth(to);
    }
    if (period === "custom") {
      return customActive ? dayMonth(dateFrom) + " → " + dayMonth(dateTo) : "—";
    }
    return period === "hoje" ? "hoje" : "últimos " + period;
  })();

  // ── Render ──
  return (
    <div className={classes.root}>
      <GlobalSpinStyle />

      {/* ───── Z0 TOP BAR ───── */}
      <div className={classes.topBar}>
        <div>
          <div className={classes.breadcrumb}>
            Multi unidades&nbsp;/&nbsp;<span>Operacional</span>
            {groupName && (
              <span style={{ color: tokens.textMuted, fontWeight: 500 }}>
                &nbsp;·&nbsp;{groupName}
              </span>
            )}
          </div>
          {/* A idade dos números (o mais antigo deles); sem ela, a hora do cálculo. */}
          <div className={classes.updatedAgo}>
            {updatedAgo(data?.meta?.dataAsOf || data?.meta?.generatedAt)}
          </div>
        </div>
        <div className={classes.actionsCluster}>
          <GlassTooltip
            title={refreshLocked ? "Aguarde alguns segundos para atualizar de novo" : "Atualizar agora"}
            arrow
            placement="bottom"
          >
            {/* O span mantém o tooltip com o botão desabilitado. */}
            <span>
              <button
                className={classes.actionBtn}
                onClick={refreshNow}
                disabled={loading || refreshLocked}
                aria-label="Atualizar agora"
              >
                <RefreshCw
                  size={15}
                  className={loading ? classes.refreshSpinning : ""}
                />
              </button>
            </span>
          </GlassTooltip>
          <GlassTooltip title="Exportar CSV" arrow placement="bottom">
            <button className={classes.actionBtn}>
              <Download size={15} />
            </button>
          </GlassTooltip>
          <GlassTooltip title="Tela cheia" arrow placement="bottom">
            <button className={classes.actionBtn}>
              <Maximize2 size={15} />
            </button>
          </GlassTooltip>
        </div>
      </div>

      <div className={classes.contentWrap}>
        {/* ───── ERROR ───── */}
        {error && (
          <div className={classes.errorCard}>
            <AlertCircle size={18} />
            <span style={{ flex: 1 }}>{error}</span>
            <button className={classes.retryBtn} onClick={() => fetchData()}>
              Tentar novamente
            </button>
          </div>
        )}

        {/* ───── Z1 HERO STRIP ───── */}
        <div className={zone(classes.heroStrip)}>
          {/* Loading skeleton */}
          {loading && !data
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={classes.skeletonCard} />
              ))
            : (
              <>
                {/* Card 1 — Ativos Agora */}
                <div
                  className={`${classes.heroCard} ${classes.clickable}`}
                  {...openable("activeNow")}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>Ativos agora</span>
                    <LivePulseDot color={tokens.accent} size={7} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span className={classes.heroValue}>
                      {fmtNumberPtBR(activeNow)}
                    </span>
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      últimas 24h
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(activeNow || 50, 0.4)}
                        color={tokens.accent}
                        width={96}
                        height={28}
                        idSuffix="ativos"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 2 — Sem Resposta */}
                <div
                  className={`${classes.heroCard} ${classes.clickable}`}
                  {...openable("noResponse")}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>Sem resposta</span>
                    <StatusBadge status={noResponseStatus} tokens={tokens} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span
                      className={`${classes.heroValue} ${
                        noResponseStatus === "breach"
                          ? classes.heroValueBreach
                          : ""
                      }`}
                    >
                      {fmtNumberPtBR(noResponse)}
                    </span>
                    {noResponseRate > 0 && (
                      <span
                        style={{
                          fontSize: 13,
                          color: tokens.textMuted,
                          fontWeight: 600
                        }}
                      >
                        {noResponseRate}% dos ativos
                      </span>
                    )}
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      +10min sem resposta
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(noResponse || 3, 0.6)}
                        color={
                          noResponseStatus === "breach"
                            ? tokens.breach
                            : tokens.accent
                        }
                        width={96}
                        height={28}
                        idSuffix="semresp"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 3 — ART tempo real */}
                <div
                  className={`${classes.heroCard} ${classes.clickable}`}
                  {...openable("artRolling")}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>ART rolling 24h</span>
                    <StatusBadge status={artStatus} tokens={tokens} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span
                      className={`${classes.heroValue} ${
                        artStatus === "breach"
                          ? classes.heroValueBreach
                          : artStatus === "watch"
                          ? classes.heroValueWatch
                          : ""
                      }`}
                    >
                      {formatARTMinutes(artMin)}
                    </span>
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      {tierS?.art?.pairCount
                        ? fmtNumberPtBR(tierS.art.pairCount) + " pares"
                        : "sem dados"}
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(artMin || 8, 0.35)}
                        color={
                          artStatus === "breach"
                            ? tokens.breach
                            : artStatus === "watch"
                            ? tokens.watch
                            : tokens.accent
                        }
                        width={96}
                        height={28}
                        idSuffix="art"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 4 — Top Ofensores (ART) */}
                <div className={`${classes.heroCard} ${classes.clickable}`} {...openable("topOffenders")}>
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>
                      Top {Math.min(3, artRanking.length)} ofensores · ART
                      {artRanking.length > 0 && (
                        <span style={{ color: tokens.textMuted, fontWeight: 500 }}>
                          {" "}
                          / {artRanking.length} total
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={classes.topOffList} style={{ marginTop: 12 }}>
                    {artRanking.length === 0 ? (
                      <span
                        style={{
                          color: tokens.textMuted,
                          fontSize: 12,
                          fontStyle: "italic"
                        }}
                      >
                        Sem dados disponíveis.
                      </span>
                    ) : (
                      artRanking.slice(-3).reverse().map((r, i) => (
                        <div key={i} className={classes.topOffItem}>
                          <span className={classes.topOffName}>{r.name}</span>
                          <span
                            className={classes.topOffArt}
                            style={{
                              color:
                                slaStatus(r.avgMinutes * 60, limitsOf(r)) === "breach"
                                  ? tokens.breachText
                                  : slaStatus(r.avgMinutes * 60, limitsOf(r)) === "watch"
                                  ? tokens.watchText
                                  : tokens.healthyText
                            }}
                          >
                            {formatARTMinutes(r.avgMinutes)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
        </div>

        {/* ───── Z2 FILTER BAR ───── */}
        <div className={classes.filterBar}>
          {busy && (
            <span className={classes.loadingBar} aria-hidden="true">
              <span className={classes.loadingBarFill} />
            </span>
          )}
          <div className={classes.periodSegmented}>
            {["hoje", "7d", "30d"].map(p => (
              <button
                key={p}
                className={`${classes.periodBtn} ${
                  period === p ? classes.periodBtnActive : ""
                }`}
                onClick={() => setPeriod(p)}
              >
                {p === "hoje" ? "Hoje" : p}
              </button>
            ))}
            {/* Filtro livre de data: alterna pro modo personalizado */}
            <button
              className={`${classes.periodBtn} ${
                period === "custom" ? classes.periodBtnActive : ""
              }`}
              onClick={() => setPeriod("custom")}
              title="Período personalizado"
              style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <Calendar size={12} />
              Personalizado
            </button>
          </div>

          {period === "custom" && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginLeft: 4
              }}
            >
              <input
                type="date"
                className={classes.dateInput}
                value={dateFrom}
                max={dateTo || undefined}
                onChange={e => setDateFrom(e.target.value)}
                title="Data início"
              />
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>→</span>
              <input
                type="date"
                className={classes.dateInput}
                value={dateTo}
                min={dateFrom || undefined}
                onChange={e => setDateTo(e.target.value)}
                title="Data fim"
              />
              {rangeTooLong ? (
                <span style={{ color: tokens.breachText, fontSize: 11, fontWeight: 600 }}>
                  máximo {MAX_RANGE_DAYS} dias
                </span>
              ) : !customActive ? (
                <span style={{ color: tokens.watch, fontSize: 11, fontWeight: 600 }}>
                  selecione as 2 datas
                </span>
              ) : (
                <span style={{ color: tokens.textMuted, fontSize: 11 }}>
                  {rangeDays} dia(s)
                </span>
              )}
            </div>
          )}

          <DepartmentScopeSelect
            departmentScope={departmentScope}
            baseLabel={i18n.t("dashboard.scope.units")}
            className={classes.scope}
            labelClassName={classes.scopeLabel}
            selectClassName={classes.scopeSelect}
          />

          <div className={classes.searchWrap}>
            <Search size={14} className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              placeholder="Buscar atendente..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ───── Z3 PERFORMANCE TABLE ───── */}
        <div style={{ marginBottom: 8 }} className={classes.chartsHeader}>
          <span className={classes.titleCluster}>
            <span className={classes.chartsTitle}>Performance · atendentes</span>
            <span
              className={`${classes.rangeChip}${busy ? ` ${classes.rangeChipBusy}` : ""}`}
              aria-live="polite"
            >
              {busy ? (
                <>
                  <RefreshCw size={11} className={classes.refreshSpinning} />
                  Atualizando…
                </>
              ) : (
                <>
                  <Calendar size={11} />
                  {appliedRange}
                </>
              )}
            </span>
          </span>
        </div>
        <div className={zone(classes.tableCard)}>
          <div className={classes.tableHeader}>
            <span />
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("name")}
            >
              Atendente {renderSortIcon("name")}
            </span>
            <span className={classes.headerWithHelp}>
              Status
              <GlassTooltip
                title={statusHelp(limitsVary ? null : artLimits)}
                arrow
                placement="top"
                enterTouchDelay={0}
                leaveTouchDelay={8000}
              >
                <button type="button" className={classes.helpIcon} aria-label="Como o status é calculado">
                  <Info size={12} />
                </button>
              </GlassTooltip>
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("tickets")}
            >
              Atendimentos {renderSortIcon("tickets")}
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("messages")}
            >
              Mensagens {renderSortIcon("messages")}
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("avgSupportTime")}
              title="Tempo médio de resposta em horário útil (par mensagem→resposta)"
            >
              ART (horário útil) {renderSortIcon("avgSupportTime")}
            </span>
            <span>Hoje</span>
            <span />
          </div>
          {loading && !data ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={classes.tableRow}
                style={{ opacity: 0.4, pointerEvents: "none" }}
              >
                <span />
                <span
                  className={classes.tableCellName}
                  style={{ color: tokens.textMuted }}
                >
                  carregando…
                </span>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            ))
          ) : filteredAttendants.length === 0 ? (
            <div className={classes.emptyState}>
              {performanceError
                ? "Não foi possível carregar os atendentes agora. A tela tenta de novo sozinha em instantes."
                : "Nenhum atendente encontrado nesse filtro."}
            </div>
          ) : (
            pagedAttendants.map((att, i) => {
              const rowLimits = limitsOf(att);
              const status =
                att.avgSupportTime == null
                  ? "empty"
                  : slaStatus(Math.max(att.avgSupportTime, 1), rowLimits);
              const rowAccounts = accountsOf(att);
              const dotColor =
                status === "healthy"
                  ? tokens.healthy
                  : status === "watch"
                  ? tokens.watch
                  : status === "breach"
                  ? tokens.breach
                  : tokens.textMuted;
              const breakdown = Array.isArray(att.breakdown) ? att.breakdown : [];
              const hasBreakdown = breakdown.length > 1;
              const tooltipText = hasBreakdown
                ? "Detalhamento por empresa:\n" +
                  breakdown
                    .map(b => `${b.companyName}: ${fmtNumberPtBR(b.tickets)} tickets · ART ${secToHms(b.avgSupportTime)}`)
                    .join("\n")
                : `${att.name}: ${fmtNumberPtBR(att.tickets || 0)} tickets · ART ${secToHms(att.avgSupportTime)}`;

              const nameCell = (
                <span
                  className={classes.tableCellName}
                  style={{
                    cursor: hasBreakdown ? "help" : "default",
                    textDecoration: hasBreakdown ? "underline dotted" : "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6
                  }}
                  title={att.name}
                >
                  {att.name}
                  {att.isAI && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: tokens.accentSoft,
                        color: tokens.accent,
                        letterSpacing: "0.05em"
                      }}
                    >
                      IA
                    </span>
                  )}
                </span>
              );

              return (
                <div key={i} className={classes.tableRow}>
                  <span
                    className={classes.statusDot}
                    style={{ background: dotColor }}
                  />
                  {hasBreakdown ? (
                    <GlassTooltip title={tooltipText} arrow placement="top">
                      {nameCell}
                    </GlassTooltip>
                  ) : (
                    nameCell
                  )}
                  {limitsVary ? (
                    <GlassTooltip
                      title={
                        (rowAccounts.length > 1
                          ? "Limites mais rigorosos entre as contas da pessoa: "
                          : "Limites da conta: ") + limitsText(rowLimits)
                      }
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                      leaveTouchDelay={8000}
                    >
                      <span className={classes.statusCell} tabIndex={0}>
                        <StatusBadge status={status} tokens={tokens} />
                      </span>
                    </GlassTooltip>
                  ) : (
                    <span className={classes.statusCell}>
                      <StatusBadge status={status} tokens={tokens} />
                    </span>
                  )}
                  <BarInCell
                    value={Number(att.tickets) || 0}
                    max={maxTickets}
                    color={tokens.accent}
                    bg={tokens.surfaceBg}
                    height={5}
                    textColor={tokens.textPrimary}
                    label={fmtNumberPtBR(att.tickets || 0)}
                  />
                  <span
                    className={classes.tableCellNum}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <MessageSquare size={11} style={{ color: tokens.textMuted }} />
                    {fmtNumberPtBR(att.messages || 0)}
                  </span>
                  <span
                    className={classes.tableCellNum}
                    title={
                      att.avgSupportTime == null
                        ? "Sem expediente configurado ou sem respostas no período"
                        : undefined
                    }
                  >
                    {att.avgSupportTime === 0 ? "00:00" : secToHms(att.avgSupportTime)}
                  </span>
                  <span className={classes.tableCellNum}>
                    {fmtNumberPtBR(att.ticketsToday != null ? att.ticketsToday : 0)}
                  </span>
                  <button
                    className={classes.actionBtn}
                    style={{ padding: 6 }}
                    title="Ver detalhamento por empresa"
                    onClick={() => setDetailRow(att)}
                  >
                    <Eye size={14} />
                  </button>
                </div>
              );
            })
          )}
          {/* Paginação */}
          {filteredAttendants.length > PAGE_SIZE && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderTop: "1px solid " + tokens.cardBorder,
                background: tokens.cardBgStrong,
                fontSize: 12,
                color: tokens.textMuted
              }}
            >
              <span>
                Mostrando{" "}
                <strong style={{ color: tokens.textPrimary }}>
                  {(safePage - 1) * PAGE_SIZE + 1}
                  –
                  {Math.min(safePage * PAGE_SIZE, filteredAttendants.length)}
                </strong>{" "}
                de{" "}
                <strong style={{ color: tokens.textPrimary }}>
                  {filteredAttendants.length}
                </strong>{" "}
                atendentes
              </span>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <button
                  className={classes.actionBtn}
                  style={{ padding: 4, opacity: safePage <= 1 ? 0.35 : 1 }}
                  disabled={safePage <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  title="Anterior"
                >
                  <ChevronLeft size={14} />
                </button>
                <span style={{ minWidth: 60, textAlign: "center" }}>
                  Pág. {safePage} / {totalPages}
                </span>
                <button
                  className={classes.actionBtn}
                  style={{ padding: 4, opacity: safePage >= totalPages ? 0.35 : 1 }}
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  title="Próxima"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ───── Z4 CHARTS STRIP ───── */}
        {charts && (
          <>
            <div className={classes.chartsHeader}>
              <span className={classes.chartsTitle}>
                Histórico · últimos 12 meses
              </span>
            </div>
            <div className={zone(classes.chartsGrid)}>
              <ChartCardItem
                label="Casos sem resposta"
                series={charts.openNoReply}
                onOpen={() => setOpenCard("openNoReply")}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Maior tempo sem resposta"
                series={charts.longestNoReplyAttendantSec}
                onOpen={() => setOpenCard("longestNoReplyAttendantSec")}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Tempo até a última resposta"
                series={charts.avgFirstReplyAttendantSec}
                onOpen={() => setOpenCard("avgFirstReplyAttendantSec")}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Maior tempo até a última resposta"
                series={charts.longestFirstReplySec}
                onOpen={() => setOpenCard("longestFirstReplySec")}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="ART médio · cliente"
                series={charts.avgFirstReplyClientSec}
                onOpen={() => setOpenCard("avgFirstReplyClientSec")}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Casos abertos no mês"
                series={charts.totalOpen}
                onOpen={() => setOpenCard("totalOpen")}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Aguardando resposta do cliente"
                series={charts.waitingClient}
                onOpen={() => setOpenCard("waitingClient")}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Duração média da interação"
                series={charts.avgInteractionSec}
                onOpen={() => setOpenCard("avgInteractionSec")}
                isTime
                tokens={tokens}
                classes={classes}
              />
            </div>

            {charts.totalDaily && (
              <>
                <div className={classes.chartsHeader} style={{ marginTop: 24 }}>
                  <span className={classes.chartsTitle}>
                    Atendimentos · últimos 30 dias
                  </span>
                </div>
                <div className={`${classes.chartCard} ${classes.clickable}`} style={{ minHeight: 240 }} {...openable("totalDaily")}>
                  <div className={classes.chartHeaderRow}>
                    <span className={classes.chartTitle}>Tickets por dia</span>
                    <span className={classes.chartValue}>
                      {fmtNumberPtBR(
                        charts.totalDaily[charts.totalDaily.length - 1]?.value || 0
                      )}
                    </span>
                  </div>
                  <div className={classes.chartBody} style={{ minHeight: 160 }}>
                    <Sparkline
                      data={charts.totalDaily}
                      color={tokens.accent}
                      width={1200}
                      height={160}
                      idSuffix="daily"
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ───── Modal detalhamento por empresa ───── */}
      <CardDetailsDialog
        cardKey={openCard}
        onClose={() => setOpenCard(null)}
        tokens={tokens}
        actionBtnClass={classes.actionBtn}
        scopeParams={scopeParams}
        charts={charts}
        artRanking={artRanking}
        errorText={apiErrorText}
        refreshKey={data?.meta?.generatedAt}
      />

      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            background: tokens.modalBg,
            border: "1px solid " + tokens.cardBorder,
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
          }
        }}
      >
        {detailRow && (
          <div style={{ padding: 0 }}>
            {/* Header */}
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid " + tokens.cardBorder,
                background: tokens.modalHeaderBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: tokens.textMuted,
                    marginBottom: 4
                  }}
                >
                  Detalhamento · {appliedRange}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: tokens.textPrimary,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8
                  }}
                >
                  {detailRow.name}
                  {detailRow.isAI && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: tokens.accentSoft,
                        color: tokens.accent,
                        letterSpacing: "0.05em"
                      }}
                    >
                      IA
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: tokens.textMuted,
                    marginTop: 4
                  }}
                >
                  Presente em{" "}
                  <strong style={{ color: tokens.textSecondary }}>
                    {detailRow.totalCompanies ||
                      (detailRow.breakdown || []).length}{" "}
                    empresa(s)
                  </strong>
                </div>
              </div>
              <button
                className={classes.actionBtn}
                style={{ padding: 6 }}
                onClick={() => setDetailRow(null)}
                title="Fechar"
              >
                <XIcon size={16} />
              </button>
            </div>

            {/* Totais agregados */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                padding: "16px 24px",
                borderBottom: "1px solid " + tokens.cardBorder
              }}
            >
              {[
                {
                  label: "Atendimentos",
                  value: fmtNumberPtBR(detailRow.tickets || 0)
                },
                {
                  label: "Mensagens",
                  value: fmtNumberPtBR(detailRow.messages || 0)
                },
                {
                  label: "ART médio (horário útil)",
                  value: secToHms(detailRow.avgSupportTime)
                }
              ].map((s, idx) => (
                <div
                  key={idx}
                  style={{
                    background: tokens.surfaceBg,
                    border: "1px solid " + tokens.cardBorder,
                    borderRadius: 10,
                    padding: "10px 12px"
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: tokens.textMuted,
                      marginBottom: 4
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: tokens.textPrimary,
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Tabela breakdown por empresa */}
            <div style={{ padding: "16px 24px 24px" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: tokens.textMuted,
                  marginBottom: 10
                }}
              >
                Por empresa
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px,1fr) 90px 100px 130px",
                  gap: 10,
                  padding: "8px 12px",
                  background: tokens.cardBgStrong,
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: tokens.textMuted
                }}
              >
                <span>Empresa</span>
                <span style={{ textAlign: "right" }}>Atend.</span>
                <span style={{ textAlign: "right" }}>Mensagens</span>
                <span style={{ textAlign: "right" }}>ART (h. útil)</span>
              </div>
              {(detailRow.breakdown || []).length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    fontSize: 13,
                    color: tokens.textMuted,
                    fontStyle: "italic",
                    textAlign: "center"
                  }}
                >
                  Sem dados de breakdown.
                </div>
              ) : (
                (detailRow.breakdown || []).map((b, idx) => {
                  const dkey = `${b.userId}-${b.companyId}`;
                  const isOpen = drillKey === dkey;
                  const drill = drillCache[dkey];
                  return (
                    <React.Fragment key={idx}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(180px,1fr) 90px 100px 130px",
                          gap: 10,
                          padding: "10px 12px",
                          borderBottom: isOpen
                            ? "none"
                            : "1px solid " + tokens.cardBorder,
                          alignItems: "center"
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: tokens.textPrimary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                          title={b.companyName}
                        >
                          {b.companyName}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            textAlign: "right",
                            color: tokens.textPrimary,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums"
                          }}
                        >
                          {fmtNumberPtBR(b.tickets || 0)}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            textAlign: "right",
                            color: tokens.textSecondary,
                            fontVariantNumeric: "tabular-nums"
                          }}
                        >
                          {fmtNumberPtBR(b.messages || 0)}
                        </span>
                        {/* ART clicável → drill-down lazy dos tickets */}
                        <span
                          onClick={() => toggleDrill(b)}
                          title="Ver tickets que compõem este ART"
                          style={{
                            fontSize: 13,
                            textAlign: "right",
                            color: isOpen ? tokens.accent : tokens.textPrimary,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            cursor: "pointer",
                            textDecoration: "underline dotted",
                            textUnderlineOffset: 3,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 4
                          }}
                        >
                          {isOpen ? (
                            <ChevronDown size={11} />
                          ) : (
                            <ChevronRight size={11} />
                          )}
                          {secToHms(b.avgSupportTime)}
                        </span>
                      </div>

                      {/* Painel drill-down: tickets + tempos (lazy) */}
                      {isOpen && (
                        <div
                          style={{
                            padding: "8px 12px 14px",
                            background: tokens.surfaceBg,
                            borderBottom: "1px solid " + tokens.cardBorder
                          }}
                        >
                          {!drill || drill.loading ? (
                            <div style={{ fontSize: 12, color: tokens.textMuted, padding: 8 }}>
                              Carregando tickets…
                            </div>
                          ) : drill.error ? (
                            <div style={{ fontSize: 12, color: tokens.breachText, padding: 8 }}>
                              {drill.error}
                            </div>
                          ) : drill.scheduleConfigured === false ? (
                            <div style={{ fontSize: 12, color: tokens.watch, padding: 8, fontStyle: "italic" }}>
                              Sem expediente configurado nesta empresa — não dá pra calcular o tempo de resposta em horário útil. Configure em Configurações &gt; Horários.
                            </div>
                          ) : !drill.tickets || drill.tickets.length === 0 ? (
                            <div style={{ fontSize: 12, color: tokens.textMuted, padding: 8, fontStyle: "italic" }}>
                              Nenhum ticket com atividade do cliente neste período.
                            </div>
                          ) : (
                            <>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "minmax(150px,1fr) 90px 130px",
                                  gap: 10,
                                  padding: "4px 8px",
                                  fontSize: 9,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  color: tokens.textMuted
                                }}
                              >
                                <span>Ticket</span>
                                <span style={{ textAlign: "right" }}>Status</span>
                                <span style={{ textAlign: "right" }}>Tempo resposta</span>
                              </div>
                              {drill.tickets.map(tk => {
                                const closed = tk.status === "closed";
                                const respLabel =
                                  tk.responseTime != null
                                    ? secToHms(tk.responseTime)
                                    : "sem resposta";
                                return (
                                  <div
                                    key={tk.ticketId}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(150px,1fr) 90px 130px",
                                      gap: 10,
                                      padding: "6px 8px",
                                      alignItems: "center",
                                      borderTop: "1px solid " + tokens.cardBorder
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 12,
                                        color: tokens.textPrimary,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                      }}
                                      title={`#${tk.ticketId} · ${tk.contactName}${tk.contactNumber ? " · " + tk.contactNumber : ""}`}
                                    >
                                      {tk.contactName}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        textAlign: "right",
                                        color: closed ? tokens.textMuted : tokens.accent,
                                        fontWeight: 600
                                      }}
                                    >
                                      {closed ? "fechado" : "aberto"}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        textAlign: "right",
                                        color:
                                          tk.responseTime != null
                                            ? tokens.textPrimary
                                            : tokens.textMuted,
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums"
                                      }}
                                      title={
                                        tk.pendingCount > 0
                                          ? `${tk.pendingCount} mensagem(ns) do cliente sem resposta`
                                          : `${tk.responsesCount} resposta(s)`
                                      }
                                    >
                                      {respLabel}
                                    </span>
                                  </div>
                                );
                              })}
                              <div style={{ fontSize: 10, color: tokens.textMuted, padding: "8px 8px 0", fontStyle: "italic" }}>
                                Tempo de resposta em horário útil (mesma base do ART). Ordenado do maior pro menor · {drill.tickets.length} ticket(s).
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-componente local: card de gráfico histórico
// ─────────────────────────────────────────────────────────────────────
const ChartCardItem = ({ label, series, isTime, tokens, classes, onOpen }) => {
  if (!series || series.length === 0) {
    return (
      <div className={classes.chartCard}>
        <div className={classes.chartHeaderRow}>
          <span className={classes.chartTitle}>{label}</span>
        </div>
        <div className={classes.emptyState} style={{ padding: 32 }}>
          Sem dados.
        </div>
      </div>
    );
  }
  const lastVal = series[series.length - 1]?.value ?? 0;
  const fmt = isTime ? secToShort : fmtNumberPtBR;
  return (
    <div className={`${classes.chartCard} ${classes.clickable}`} {...openableProps(onOpen)}>
      <div className={classes.chartHeaderRow}>
        <span className={classes.chartTitle}>{label}</span>
        <span className={classes.chartValue}>{fmt(lastVal)}</span>
      </div>
      <div className={classes.chartBody}>
        <Sparkline
          data={series}
          color={tokens.accent}
          width={600}
          height={120}
          idSuffix={label}
        />
      </div>
    </div>
  );
};

export default MultiUnitV2;
